import { Observable,Subject, of} from 'rxjs';
import { KeepAliveService } from './keep-alive.service';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';

interface CacheContent {
	expiry: number;
	value: any;
}

/**
 * Cache Service is an observables based in-memory cache implementation
 * Keeps track of in-flight observables and sets a default expiry for cached values
 * @export
 * @class CacheService
 */
@Injectable()
export class EricCacheService {
	private cache: Map<string, CacheContent> = new Map<string, CacheContent>();
	private inFlightObservables: Map<string, Subject<any>> = new Map<
		string,
		Subject<any>
	>();
	readonly LOG_TO_CONSOLE = false;
	readonly DEFAULT_MAX_AGE: number = 300000; // 300000 is five minutes;

	constructor(private keepAlive: KeepAliveService) {}
	/**
	 * Gets the value from cache if the key is provided.
	 * If no value exists in cache, then check if the same call exists
	 * in flight, if so return the subject. If not create a new
	 * Subject inFlightObservable and return the source observable.
	 */
	get(
		key: string,
		fallback?: Observable<any>,
		maxAge?: number
	): Observable<any> | Subject<any> {
		let observable: Observable<any>;
		let subject: Subject<any>;

		if (this.hasValidCachedValue(key)) {
			if (this.LOG_TO_CONSOLE) {
				console.log(`%cGetting from cache ${key}`, 'color: green');
			}
			this.keepAlive.touch();
			observable = of(this.cache.get(key).value); // .delay(500); // simulate fetch with a delay.
		}

		if (!observable) {
			if (!maxAge) {
				maxAge = this.DEFAULT_MAX_AGE;
			}
			if (this.inFlightObservables.has(key)) {
				subject = this.inFlightObservables.get(key);
			} else if (fallback && fallback instanceof Observable) {
				if (this.LOG_TO_CONSOLE) {
					console.log(`%c Calling api for ${key}`, 'color: purple');
				}
				observable = fallback.pipe(tap(value => {
					this.set(key, value, maxAge);
				}));
			} else {
				observable = Observable.throw(
					'Requested key is not available in Cache'
				);
			}
		}

		if (observable) {
			return observable;
		} else {
			return subject;
		}
	}

	/**
	 * Sets the value with key in the cache
	 * Notifies all observers of the new value
	 */
	set(key: string, value: any, maxAge: number = this.DEFAULT_MAX_AGE): void {
		this.inFlightObservables.set(key, new Subject());
		this.cache.set(key, { value: value, expiry: Date.now() + maxAge });
		this.notifyInFlightObservers(key, value);
	}

	/**
	 * Checks if the a key exists in cache
	 */
	has(key: string): boolean {
		return this.cache.has(key);
	}

	/**
	 * Publishes the value to all observers of the given
	 * in progress observables if observers exist.
	 */
	private notifyInFlightObservers(key: string, value: any): void {
		if (this.inFlightObservables.has(key)) {
			const inFlight = this.inFlightObservables.get(key);
			const observersCount = inFlight.observers.length;
			if (observersCount) {
				if (this.LOG_TO_CONSOLE) {
					console.log(
						`%cNotifying ${
							inFlight.observers.length
						} flight subscribers for ${key}`,
						'color: blue'
					);
				}
				inFlight.next(value);
			}
			inFlight.complete();
			this.inFlightObservables.delete(key);
		}
	}

	/**
	 * Checks if the key exists and   has not expired.
	 */
	private hasValidCachedValue(key: string): boolean {
		if (this.cache.has(key)) {
			if (this.cache.get(key).expiry < Date.now()) {
				this.cache.delete(key);
				return false;
			}
			return true;
		} else {
			return false;
		}
	}

	stringify(obj: object): string {
		// const o = Object(obj)
		// 	.reduce((acc, [k, v]) => (v==null ? acc : {...acc, [k]: v}), {});
		const o = Object.keys(obj)
			.filter(function(k) {
				return obj[k] != null;
			})
			.reduce(function(acc, k) {
				acc[k] = obj[k];
				return acc;
			}, {});
		return JSON.stringify(o);
	}
}
